import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { searchWords } from '@/lib/supabase/search-filter'
import type { Generation } from './generations'
import type { Tag } from './tags'

// =============================================================================
// Video Types
// =============================================================================

type VideoRow = Database['public']['Tables']['videos']['Row']

/**
 * Video with current generation and author profile.
 * Videos are domain entities; generations are their versions/takes.
 * current_generation embeds the full Generation including its author profile.
 */
export interface Video extends VideoRow {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  current_generation: Generation | null
  video_tags: { tags: Tag }[]
}

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

export interface VideoFilters {
  authorId?: string | null
  modelId?: string | null
  searchQuery?: string | null
  tagIds?: string[] | null
}

const VIDEO_SELECT = `
  *,
  profiles!author(id, email, full_name, avatar_url),
  current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
  video_tags(tags(id, name, color))
`

/**
 * Apply video filters to any Supabase query builder.
 * Works with both select and update queries.
 */
function applyVideoFilters<
  T extends {
    eq: (col: string, val: string) => T
    is: (col: string, val: null) => T
    ilike: (col: string, val: string) => T
    contains: (col: string, val: string[]) => T
  },
>(query: T, filters?: VideoFilters): T {
  let q = query

  if (filters?.authorId) {
    q = q.eq('author', filters.authorId)
  }

  // Every selected label must be present.
  if (filters?.tagIds && filters.tagIds.length > 0) {
    q = q.contains('tag_ids', filters.tagIds)
  }

  if (filters?.modelId) {
    q = q.eq('model_id', filters.modelId)
  }

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.ilike('name', `%${word}%`)
    }
  }

  return q
}

export const video = {
  findOne: (videoId: string) =>
    queryOptions({
      queryKey: ['video', videoId],
      queryFn: async (): Promise<Video | null> => {
        if (!videoId || videoId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('videos')
          .select(VIDEO_SELECT)
          .eq('id', videoId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch video: ${error.message}`, {
            cause: error,
          })
        }

        return data as Video | null
      },
    }),

  findManyByWorkspace: (
    workspaceId: string,
    filters?: VideoFilters,
    options?: { pageSize?: number }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'videos',
        workspaceId,
        filters ?? {},
        options?.pageSize ?? PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Video[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch videos without valid workspace ID'
          )
        }

        const pageSize = options?.pageSize ?? PAGE_SIZE
        const from = pageParam * pageSize
        const to = from + pageSize - 1

        const baseQuery = supabase
          .from('videos')
          .select(VIDEO_SELECT)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to)

        const { data, error } = await applyVideoFilters(baseQuery, filters)

        if (error) {
          throw new Error(`Failed to fetch videos: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as Video[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = options?.pageSize ?? PAGE_SIZE
        return lastPage.length === pageSize ? allPages.length : undefined
      },
    }),

  /**
   * Videos whose current generation is a playable clip. The clip library
   * uses this so failed or still-rendering takes never surface there.
   */
  findManyCompletedByWorkspace: (workspaceId: string, filters?: VideoFilters) =>
    infiniteQueryOptions({
      queryKey: ['videos', workspaceId, 'completed', filters ?? {}, PAGE_SIZE],
      queryFn: async ({ pageParam = 0 }): Promise<Video[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch videos without valid workspace ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        const baseQuery = supabase
          .from('videos')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            video_tags(tags(id, name, color))
          `
          )
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to)

        const { data, error } = await applyVideoFilters(baseQuery, filters)

        if (error) {
          throw new Error(`Failed to fetch videos: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as Video[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  /**
   * Update video metadata (name)
   */
  update: async (videoId: string, input: { name?: string }): Promise<void> => {
    const { error } = await supabase
      .from('videos')
      .update({
        ...(input.name !== undefined && { name: input.name }),
      })
      .eq('id', videoId)

    if (error) {
      throw new Error(`Failed to update video: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete a video
   */
  delete: async (videoId: string): Promise<void> => {
    if (!videoId || videoId.trim() === '') {
      throw new Error('Invalid videoId: cannot delete video without valid ID')
    }

    const { error } = await supabase
      .from('videos')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', videoId)

    if (error) {
      throw new Error(`Failed to delete video: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete all videos in a workspace matching the given filters,
   * minus any explicitly excluded ids (select-all-with-exclusions).
   */
  deleteAllByWorkspace: async (
    workspaceId: string,
    filters?: VideoFilters,
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    let baseQuery = supabase
      .from('videos')
      .update({ deleted_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .is('deleted_at', null)

    baseQuery = applyVideoFilters(baseQuery, filters)

    if (excludeIds && excludeIds.length > 0) {
      baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
    }

    const { error } = await baseQuery

    if (error) {
      throw new Error(`Failed to delete videos: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete multiple videos by IDs
   */
  deleteMany: async (ids: string[]): Promise<void> => {
    if (!ids.length) {
      return
    }

    const { error } = await supabase
      .from('videos')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', ids)

    if (error) {
      throw new Error(`Failed to delete videos: ${error.message}`, {
        cause: error,
      })
    }
  },
}

/**
 * Find a video in any workspace infinite query cache.
 * For use in route loaders (non-hook context).
 */
export function findCachedVideo(
  queryClient: QueryClient,
  workspaceId: string,
  videoId: string
): Video | null {
  const queries = queryClient.getQueriesData<InfiniteData<Video[]>>({
    queryKey: ['videos', workspaceId],
    exact: false,
  })

  for (const [, data] of queries) {
    const found = data?.pages.flat().find((v) => v.id === videoId)
    if (found) {
      return found
    }
  }
  return null
}
